import {
  CartProduct,
  isSameVehicle,
  PartsLocations,
  productsMatch,
  SellerCartVehicle,
  SellerWithLocations,
} from 'cart'
import {
  AvailabilityErrorType,
  ProductLocationModel,
  ProductModel,
} from 'interfaces'
import { DeliveryDetail, Transport } from 'src/api/cart/interfaces'
import { IS_INTEGRATED_VERSION } from 'src/config/ConfigManager'
import { ISeller } from 'src/services/MultiSellerServicesProvider'
import { LaborItem } from 'src/store/models/LaborModel'
import { OrderFormData } from 'src/store/models/ShoppingCartModels'
import { Vehicle } from 'src/store/models/Vehicles'
import { VehicleSpecificationCartItem } from 'src/store/models/VehicleSpecification'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { RootState } from 'store'
import { getInvalidVehicles } from '../utils/Utils'

export const findCartVehicle = (
  state: RootState,
  vehicle: Vehicle
): SellerCartVehicle | undefined => {
  return state?.cart?.cartData?.vehicles?.find((item) =>
    isSameVehicle(item.vehicle, vehicle)
  )
}

export const isValidPart = (p: CartProduct): boolean => {
  const excludedTypes = new Set([
    AvailabilityErrorType.NOT_TRANSFERRED,
    AvailabilityErrorType.CANT_BE_FULFILLED,
    AvailabilityErrorType.MIN_QTY_CHANGE,
  ])
  return !p.errors?.some((e) => excludedTypes.has(e?.errorType))
}

// Common function to extract all valid parts
export const getAllValidParts = (
  state: RootState,
  vehicle: Vehicle,
  coverageKey?: string,
  locationId?: string
): CartProduct[] => {
  const cartVehicle = findCartVehicle(state, vehicle)
  if (!cartVehicle) return []

  const validParts: CartProduct[] = []

  cartVehicle.sellers?.forEach((seller) => {
    if (coverageKey !== undefined && seller.coverageKey !== coverageKey) return

    seller.locations?.forEach((location) => {
      if (
        locationId &&
        location.locationId.toString() !== locationId.toString()
      ) {
        return
      }
      if (!Array.isArray(location.parts)) return

      location.parts.forEach((part) => {
        if (!isValidPart(part)) return

        validParts.push(
          IS_INTEGRATED_VERSION
            ? {
                ...part,
                location: part.location.map((l) => ({
                  ...l,
                  isSelected: l.locationId === location.locationId,
                })),
              }
            : part
        )
      })
    })
  })

  return validParts
}

export const getVehicleOrderFormData = (
  state: RootState,
  vehicle: Vehicle
): OrderFormData => {
  const cartVehicle = state.cart.cartData.vehicles.find((item) =>
    isSameVehicle(item.vehicle, vehicle)
  )
  if (!cartVehicle) {
    return {
      customerName: '',
      noteToStore: '',
      personalNote: '',
      poNumber: '',
    }
  }
  return cartVehicle.orderFormData
}

export const getVehicleGrandTotal = (
  state: RootState,
  vehicle: Vehicle
): number => {
  const itemsTotalCost = getItemsTotalCost(state, vehicle)
  const partsBundleSavings = getVehiclePartsBundleSavings(state, vehicle)
  const cartLaborCost = getCartLaborCost(state, vehicle)

  const vehicleGrandTotal = itemsTotalCost - partsBundleSavings + cartLaborCost

  return vehicleGrandTotal
}

const getTotalProductQtyByLocation = (
  product: ProductModel,
  location: PartsLocations
): number => {
  /*  
        This set refers to a list of availability errors. We don't show products having any of these errors in 
        the cart table. We won't count such products for arriving at cart cost, cart products count.
    */

  const totalQty = location.parts
    ?.filter((p) => productsMatch(p, product))
    .filter(isValidPart)
    .reduce((acc, p) => p.quantityRequested + acc, 0)

  return totalQty
}

export const isProductInCart = (
  state: RootState,
  product: ProductModel,
  vehicle: Vehicle,
  coverageKey: string
): boolean => {
  let cartProductQty = 0

  const cartVehicle = findCartVehicle(state, vehicle)
  const cartSeller: SellerWithLocations | undefined =
    cartVehicle?.sellers?.find((seller) => seller.coverageKey === coverageKey)

  if (!cartSeller) {
    return false
  }

  for (const location of cartSeller.locations) {
    cartProductQty += getTotalProductQtyByLocation(product, location)
  }

  return cartProductQty > 0 ? true : false
}

export const totalProductsQtyInCart = (state: RootState): number => {
  const cartData = state.cart.cartData
  if (!cartData?.vehicles) return 0

  let totalParts = 0

  cartData.vehicles.forEach((vehicle) => {
    const validParts = getAllValidParts(state, vehicle.vehicle)

    validParts.forEach((part) => {
      totalParts += part.quantityRequested
    })
  })

  return totalParts
}

export const getVehiclePartsTotalCost = (
  state: RootState,
  vehicle: Vehicle
): number => {
  let partsTotalCost = 0

  const validParts = getAllValidParts(state, vehicle)

  validParts.forEach((part) => {
    // If bundleInfo exists calculate bundle prices
    if (Array.isArray(part.bundleInfo) && part.bundleInfo.length > 0) {
      part.bundleInfo.forEach((b) => {
        const qty = b.quantityRequested || 1
        partsTotalCost += b.bundled
          ? (b.bundlePrice ?? 0) * qty
          : (b.unitCostPrice ?? 0) * qty
      })

      return
    }

    // If bundleInfo doesn't exists calculate previous price values based on price breaks or selected location cost
    const selectedLocation = part.location?.find((loc) => loc.isSelected)
    if (!selectedLocation) return

    const costAfterPriceBreaks = () => {
      const priceBreaks = selectedLocation.priceBreaks || []

      if (priceBreaks.length > 0) {
        const lastBreak = priceBreaks[priceBreaks.length - 1]

        for (const priceBreak of priceBreaks) {
          if (
            priceBreak.minQty <= part.quantityRequested &&
            priceBreak.maxQty >= part.quantityRequested
          ) {
            return priceBreak.unitCost
          }
        }

        if (lastBreak?.maxQty && part.quantityRequested > lastBreak.maxQty) {
          return lastBreak.unitCost
        }
      }

      return selectedLocation.cost || 0
    }
    partsTotalCost += part.quantityRequested * costAfterPriceBreaks()
  })

  return partsTotalCost
}

export const getVehiclePartsCoreCost = (
  state: RootState,
  vehicle: Vehicle
): number => {
  let partsCoreCost = 0

  const validParts = getAllValidParts(state, vehicle)

  validParts.forEach((part) => {
    // If no bundleInfo use previous logic for core price calculation
    part.location?.forEach((loc) => {
      if (loc.isSelected) {
        const qty = part.quantityRequested ?? 1
        const core = loc.coreCost ?? 0
        partsCoreCost += core * qty
      }
    })
  })

  return partsCoreCost
}

/**
 * Helper to return the vehicle item total cost.
 * This doesn't remove the bundle savings.
 * @param state Redux state.
 * @param vehicle Vehicle to calculate the total.
 * @returns Vehicle item total cost.
 */
export const getItemsTotalCost = (
  state: RootState,
  vehicle: Vehicle
): number => {
  return (
    getVehiclePartsTotalCost(state, vehicle) +
    getVehiclePartsCoreCost(state, vehicle) +
    getVehiclePartsBundleSavings(state, vehicle)
  )
}

export const getVehiclePartsBundleSavings = (
  state: RootState,
  vehicle: Vehicle
): number => {
  let bundleSavings = 0

  const validParts = getAllValidParts(state, vehicle)

  validParts.forEach((part) => {
    // If bundleInfo exists
    if (Array.isArray(part.bundleInfo) && part.bundleInfo.length > 0) {
      part.bundleInfo.forEach((b) => {
        const qty = b.quantityRequested ?? part.quantityRequested ?? 1

        if (
          b.bundled &&
          b.unitCostPrice !== null &&
          b.unitCostPrice !== undefined &&
          b.unitCorePrice !== null &&
          b.unitCorePrice !== undefined
        ) {
          // calculate unitCorePrice if not a bundled part
          const actualPrice = qty * (b.unitCostPrice + b.unitCorePrice)
          const bundlePrice =
            ((b.bundlePrice ?? 0) + (b?.unitCorePrice ?? 0)) * qty
          bundleSavings += actualPrice - bundlePrice
        }
      })

      return
    }
  })

  return bundleSavings
}

export const getAllCartsGrandTotal = (state: RootState): number => {
  let allCartsGrandTotal = 0

  state.cart.cartData.vehicles.forEach((cartVehicle) => {
    allCartsGrandTotal += getVehicleGrandTotal(state, cartVehicle.vehicle)
  })

  return allCartsGrandTotal
}

export const getInCartProductQty = (
  state: RootState,
  vehicle: Vehicle,
  coverageKey: string,
  product: ProductModel,
  locationId?: string
): number => {
  return getAllValidParts(state, vehicle, coverageKey, locationId)
    .filter((part) => productsMatch(part, product))
    .reduce((sum, part) => sum + part.quantityRequested, 0)
}

export const getVehicleSpecifications = (
  state: RootState,
  vehicle: Vehicle
): Array<VehicleSpecificationCartItem> => {
  const cartVehicle = findCartVehicle(state, vehicle)
  return cartVehicle ? cartVehicle.specifications : []
}

export const getVehicleLabor = (
  state: RootState,
  vehicle: Vehicle
): Array<LaborItem> => {
  const cartVehicle = findCartVehicle(state, vehicle)
  return cartVehicle ? cartVehicle.laborResults : []
}

export const getCartLaborCost = (
  state: RootState,
  vehicle: Vehicle
): number => {
  const cartVehicle = findCartVehicle(state, vehicle)
  const laborResults = cartVehicle?.laborResults
  return laborResults
    ? StoreInstances.laborStore.calculateLaborCostFromItems(laborResults)
    : 0
}

export const getAvailableOrderTypeCodes = (
  state: RootState,
  locationId: string
): string[] => {
  const orderTypes = state.cart.availableTransports
    ?.find((lt) => Number(lt.locationId) === Number(locationId))
    ?.transports.map((transport) => transport.deliveryMethod)
  //returning unique orderTypes
  return Array.from(new Set(orderTypes))
}

export const getAvailableTransports = (
  state: RootState,
  locationId: string,
  orderType: string
): Transport[] => {
  const matchingTransport = (state.cart.availableTransports || []).find(
    (lt) => Number(lt.locationId) === Number(locationId)
  )
  if (!matchingTransport) {
    return []
  }
  return matchingTransport.transports.filter(
    (t) => t.deliveryMethod === orderType
  )
}

export const getMinQtyAtLocation = (
  state: RootState,
  vehicle: Vehicle,
  seller: ISeller,
  location: ProductLocationModel,
  product: ProductModel
): number => {
  const DEFAULT_MIN_QTY = 1
  const cartVehicle = findCartVehicle(state, vehicle)
  if (!cartVehicle) return DEFAULT_MIN_QTY

  const cartSeller = cartVehicle.sellers?.find(
    (s) => s.coverageKey === seller.coverageKey
  )
  if (!cartSeller || !location) return DEFAULT_MIN_QTY

  const productLocation = cartSeller.locations?.find(
    (l) => String(l.locationId) === String(location.locationId)
  )
  if (!productLocation) return DEFAULT_MIN_QTY

  const matchingProduct = productLocation.parts?.find((p) =>
    productsMatch(p, product)
  )
  if (!matchingProduct) return DEFAULT_MIN_QTY

  const matchingLocation = matchingProduct.location?.find(
    (l) => String(l.locationId) === String(location.locationId)
  )

  return matchingLocation?.minQty ?? DEFAULT_MIN_QTY
}

export const findFirstSimilarCartVehicle = (
  state: RootState,
  vehicle: Vehicle
): SellerCartVehicle | undefined => {
  return state.cart.cartData.vehicles.find(
    (item) =>
      item?.vehicle?.year?.id === vehicle?.year?.id &&
      item?.vehicle?.make?.id === vehicle?.make?.id &&
      item?.vehicle?.model?.id === vehicle?.model?.id &&
      item?.vehicle?.subModel?.id === vehicle?.subModel?.id &&
      item?.vehicle?.engine?.id === vehicle?.engine?.id
  )
}

export const getVehicleProductsCantbeFulfilledErrors = (
  state: RootState,
  vehicle: Vehicle
): CartProduct[] => {
  return findCartVehicle(state, vehicle)?.productsCantbeFulfilled || []
}

export const getLocationDeliveryDetail = (
  state: RootState,
  vehicle: Vehicle,
  coverageKey: string,
  locationId: string
): DeliveryDetail | undefined => {
  const cartVehicle = findCartVehicle(state, vehicle)
  const seller = cartVehicle?.sellers.find((s) => s.coverageKey === coverageKey)
  return seller?.locations.find((l) => l.locationId.toString() === locationId)
    ?.deliveryDetail
}

// new functions to calculate prices in cart order summary

export const getSellerPartsTotalCost = (validParts: CartProduct[]): number => {
  let partsTotalCost = 0
  validParts.forEach((part) => {
    // If bundleInfo exists calculate bundle prices
    if (Array.isArray(part.bundleInfo) && part.bundleInfo.length > 0) {
      part.bundleInfo.forEach((b) => {
        const qty = b.quantityRequested || 1
        partsTotalCost += b.bundled
          ? (b.bundlePrice ?? 0) * qty
          : (b.unitCostPrice ?? 0) * qty
      })

      return
    }

    // If bundleInfo doesn't exists calculate previous price values based on price breaks or selected location cost
    const selectedLocation = part.location?.find((loc) => loc.isSelected)
    if (!selectedLocation) return

    const costAfterPriceBreaks = () => {
      const priceBreaks = selectedLocation.priceBreaks || []

      if (priceBreaks.length > 0) {
        const lastBreak = priceBreaks[priceBreaks.length - 1]

        for (const priceBreak of priceBreaks) {
          if (
            priceBreak.minQty <= part.quantityRequested &&
            priceBreak.maxQty >= part.quantityRequested
          ) {
            return priceBreak.unitCost
          }
        }

        if (lastBreak?.maxQty && part.quantityRequested > lastBreak.maxQty) {
          return lastBreak.unitCost
        }
      }

      return selectedLocation.cost || 0
    }
    partsTotalCost += part.quantityRequested * costAfterPriceBreaks()
  })

  return partsTotalCost
}

export const getSellerPartsCoreCost = (validParts: CartProduct[]): number => {
  let partsCoreCost = 0

  validParts.forEach((part) => {
    // If no bundleInfo use previous logic for core price calculation
    part.location?.forEach((loc) => {
      if (loc.isSelected) {
        const qty = part.quantityRequested ?? 1
        const core = loc.coreCost ?? 0
        partsCoreCost += core * qty
      }
    })
  })

  return partsCoreCost
}

export const getSellerPartsBundleSavings = (
  validParts: CartProduct[]
): number => {
  let bundleSavings = 0
  validParts.forEach((part) => {
    // If bundleInfo exists
    if (Array.isArray(part.bundleInfo) && part.bundleInfo.length > 0) {
      part.bundleInfo.forEach((b) => {
        const qty = b.quantityRequested ?? part.quantityRequested ?? 1

        if (
          b.bundled &&
          b.unitCostPrice !== null &&
          b.unitCostPrice !== undefined &&
          b.unitCorePrice !== null &&
          b.unitCorePrice !== undefined
        ) {
          // calculate unitCorePrice if not a bundled part
          const actualPrice = qty * (b.unitCostPrice + b.unitCorePrice)
          const bundlePrice =
            ((b.bundlePrice ?? 0) + (b?.unitCorePrice ?? 0)) * qty
          bundleSavings += actualPrice - bundlePrice
        }
      })

      return
    }
  })

  return bundleSavings
}

export const getSellerItemsCost = (
  state: RootState,
  vehicle: Vehicle,
  seller: SellerWithLocations
): number => {
  const validSellerParts = getAllValidParts(state, vehicle, seller.coverageKey)

  return (
    getSellerPartsTotalCost(validSellerParts) +
    getSellerPartsBundleSavings(validSellerParts)
  )
}

export const shouldShowVehicleOrderFormError = (
  state: RootState,
  vehicle: Vehicle
): boolean => {
  const cartVehicle = state.cart.cartData.vehicles.find((v) =>
    isSameVehicle(v.vehicle, vehicle)
  )

  if (!cartVehicle || !state.cart.displayCartFormErrors) {
    return false
  }
  return getInvalidVehicles([cartVehicle]).length > 0
}
